import React, { useState, useEffect, useCallback } from 'react';
import { notesApi } from '../api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Icon } from '../components/common/Icons';
import { toast } from 'react-toastify';

function formatDeletedTime(iso) {
  if (!iso) return 'recently';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  const hrs = Math.floor(diff / 3600000);
  if (days === 0) {
    if (hrs < 1) return 'just now';
    return `${hrs}h ago`;
  }
  if (days === 1) return '1d ago';
  return `${days}d ago`;
}

export default function TrashPage() {
  const [trashedNotes, setTrashedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permDeleteTarget, setPermDeleteTarget] = useState(null);
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);

  const loadTrash = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await notesApi.listTrash();
      setTrashedNotes(data || []);
    } catch {
      toast.error('Failed to load trash');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTrash();
  }, [loadTrash]);

  // Restore note
  const handleRestore = async (note) => {
    try {
      await notesApi.restore(note.id);
      toast.success(`"${note.title}" restored to ${note.categoryName || note.folderName || 'General'}`);
      setTrashedNotes(prev => prev.filter(n => n.id !== note.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to restore note');
    }
  };

  // Permanently delete single note
  const handlePermanentDelete = async () => {
    if (!permDeleteTarget) return;
    try {
      await notesApi.permanentDelete(permDeleteTarget.id);
      toast.success(`"${permDeleteTarget.title}" permanently deleted`);
      setPermDeleteTarget(null);
      setTrashedNotes(prev => prev.filter(n => n.id !== permDeleteTarget.id));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to permanently delete note');
    }
  };

  // Empty entire trash
  const handleEmptyTrash = async () => {
    try {
      await notesApi.emptyTrash();
      toast.success('Trash emptied permanently');
      setShowEmptyConfirm(false);
      setTrashedNotes([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to empty trash');
    }
  };

  return (
    <div className="trash-page-container">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <Icon name="trash" size={24} />
            <span>Trash</span>
          </h1>
        </div>

        <div className="page-header-actions">
          {trashedNotes.length > 0 && (
            <button
              type="button"
              className="btn-danger-outline"
              onClick={() => setShowEmptyConfirm(true)}
            >
              <Icon name="trash" size={15} />
              <span>Empty Trash Now</span>
            </button>
          )}
        </div>
      </div>

      {/* Retention Notice Banner */}
      <div className="trash-retention-banner">
        <div className="trash-banner-icon">
          <Icon name="clock" size={18} />
        </div>
        <div className="trash-banner-text">
          <span className="font-semibold text-primary">7-Day Automatic Retention Policy: </span>
          <span>Deleted notes remain safely recoverable for 7 days before permanent removal.</span>
        </div>
      </div>

      {/* Trash Notes Grid / List */}
      {loading ? (
        <div className="loading-state-wrap">
          <div className="spinner" />
          <p className="loading-text">Loading trash...</p>
        </div>
      ) : trashedNotes.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state-icon-wrap">
            <Icon name="trash" size={36} />
          </div>
          <h2 className="empty-state-title">Trash is empty</h2>
          <p className="empty-state-desc">
            No notes currently in trash. When you delete a note, it will appear here for 7 days before being permanently removed.
          </p>
        </div>
      ) : (
        <div className="notes-responsive-grid docs-responsive-grid">
          {trashedNotes.map(note => {
            const daysLeft = note.daysRemainingInTrash != null ? note.daysRemainingInTrash : 7;
            const isExpiringSoon = daysLeft <= 2;

            return (
              <div key={note.id} className="note-card doc-card trash-card">
                <div className="note-card-top doc-card-top">
                  <div className="note-card-folder-badge doc-card-folder-badge">
                    <Icon name="category" size={12} />
                    <span>{note.categoryName || note.folderName || 'General'}</span>
                  </div>

                  <div
                    className={`trash-expiration-pill ${isExpiringSoon ? 'urgent' : ''}`}
                    title={`${daysLeft} days remaining before permanent deletion`}
                  >
                    <Icon name="clock" size={12} />
                    <span>
                      {daysLeft === 0
                        ? 'Expires today'
                        : daysLeft === 1
                        ? '1 day left'
                        : `${daysLeft} days left`}
                    </span>
                  </div>
                </div>

                <h3 className="note-card-title doc-card-title">{note.title}</h3>
                <p className="note-card-preview doc-card-preview">{note.content}</p>

                {note.tags?.length > 0 && (
                  <div className="tags-row">
                    <span
                      className="tag-badge more"
                      title={note.tags.map(t => `#${t.startsWith('#') ? t.slice(1) : t}`).join(', ')}
                    >
                      +{note.tags.length}
                    </span>
                  </div>
                )}

                <div className="note-card-footer doc-card-footer">
                  <div className="note-card-meta doc-card-meta">
                    <span className="meta-item" title={`Deleted ${formatDeletedTime(note.deletedAt)}`}>
                      {formatDeletedTime(note.deletedAt)}
                    </span>
                  </div>

                  <div className="note-card-actions doc-card-actions">
                    <button
                      type="button"
                      className="action-btn-pill"
                      onClick={() => handleRestore(note)}
                      title="Restore Note"
                      aria-label="Restore note"
                    >
                      <Icon name="restore" size={13} />
                    </button>

                    <button
                      type="button"
                      className="action-btn-pill danger"
                      onClick={() => setPermDeleteTarget(note)}
                      title="Delete Permanently"
                      aria-label="Delete permanently"
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm Permanent Delete Single Note */}
      {permDeleteTarget && (
        <ConfirmDialog
          title={`Permanently delete "${permDeleteTarget.title}"?`}
          message="This action cannot be undone. This note will be permanently erased from your account."
          confirmLabel="Delete Forever"
          onConfirm={handlePermanentDelete}
          onCancel={() => setPermDeleteTarget(null)}
          danger
        />
      )}

      {/* Confirm Empty All Trash */}
      {showEmptyConfirm && (
        <ConfirmDialog
          title="Empty Entire Trash?"
          message={`Are you sure you want to permanently delete all ${trashedNotes.length} notes in trash? This cannot be undone.`}
          confirmLabel="Empty Trash Forever"
          onConfirm={handleEmptyTrash}
          onCancel={() => setShowEmptyConfirm(false)}
          danger
        />
      )}
    </div>
  );
}
